import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Building } from '@app/shared/models/building.interface';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableModule
} from '@angular/material/table';
import {
  AbstractAdmDataSource,
  DriverMetricsDataSource,
  PaginationParams
} from '@app/driver-metrics/model/driver-metrics-data-source';
import { AdmService } from '@services/adm.service';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdmFilterCriteria, AdmFilterService } from '@app/driver-metrics/timeseries/adm-filter.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TimezoneUtils } from '@app/shared/utils/timezoneUtils';
import { Router } from '@angular/router';
import { SensorNodeService } from '@services/sensor-node.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-driver-metrics-table',
  imports: [
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTooltipModule,
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    MatProgressSpinnerModule,
    NotificationBlockComponent,
    MatButton,
    MatIcon
  ],
  templateUrl: './driver-metrics-table.component.html',
  styleUrl: './driver-metrics-table.component.scss'
})
export class DriverMetricsTableComponent implements OnInit, OnDestroy {
  metricsTableColumns = [
    'nodeId',
    'driverIdentificationNumber',
    'gtin',
    'time',
    'activeEnergy',
    'activePower',
    'controlGearOperatingTime',
    'controlGearStartCounter',
    'controlGearExternalSupplyVoltage',
    'controlGearExternalSupplyVoltageFrequency',
    'controlGearPowerFactor',
    'controlGearOverallFailureCondition',
    'controlGearOverallFailureConditionCounter',
    'controlGearExternalSupplyUndervoltage',
    'controlGearExternalSupplyUndervoltageCounter',
    'controlGearExternalSupplyOvervoltage',
    'controlGearExternalSupplyOvervoltageCounter',
    'controlGearOutputPowerLimitation',
    'controlGearOutputPowerLimitationCounter',
    'controlGearThermalDerating',
    'controlGearThermalDeratingCounter',
    'controlGearThermalShutdown',
    'controlGearThermalShutdownCounter',
    'controlGearTemperature',
    'controlGearOutputCurrentPercent',
    'lightSourceStartCounterResettable',
    'lightSourceStartCounter',
    'lightSourceOnTimeResettable',
    'lightSourceOnTime',
    'lightSourceVoltage',
    'lightSourceCurrent',
    'lightSourceOverallFailureCondition',
    'lightSourceOverallFailureConditionCounter',
    'lightSourceShortCircuit',
    'lightSourceShortCircuitCounter',
    'lightSourceOpenCircuit',
    'lightSourceOpenCircuitCounter',
    'lightSourceThermalDerating',
    'lightSourceThermalDeratingCounter',
    'lightSourceThermalShutdown',
    'lightSourceThermalShutdownCounter',
    'lightSourceTemperature',
    'daliBusPowerSupplyStatus'
  ];

  @Input() building: Building;
  protected readonly StatusClass = StatusClass;
  dataSource: AbstractAdmDataSource<any>;

  pageSize = 20;
  pageNumber = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly admService: AdmService,
    private readonly filterService: AdmFilterService,
    private readonly nodeService: SensorNodeService,
    private readonly router: Router
  ) {}
  ngOnInit(): void {
    this.filterService.filterCriteria$.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.pageSize = 20;
      this.pageNumber = 0;
      this.loadDriverMetrics();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDriverMetrics(): void {
    const paginationParams: PaginationParams = {
      pageSize: this.pageSize,
      pageNumber: this.pageNumber
    };
    this.dataSource = this.dataSource || new DriverMetricsDataSource(this.admService, this.nodeService, this.building);
    // NOTE: If you change the formatting data received, make sure to update the units displayed in the HTML accordingly.
    this.dataSource.loadDriverMetrics(this.filterService.filterCriteriaValue, paginationParams);
  }

  loadLogsFromPaginator(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageNumber = event.pageIndex;
    this.loadDriverMetrics();
  }

  formattedDate(instant: string): string {
    const buildingTimezoneDate = TimeUtils.adjustDateToTimezone(instant, this.building.timeZone);
    return format(buildingTimezoneDate, 'dd/MM/yyyy - h:mm a');
  }

  formattedNumber(value: number): number {
    if (!value) {
      return null;
    }
    return Number(value.toFixed(2));
  }

  formattedBoolean(value: boolean): string {
    return value ? 'True' : 'False';
  }

  display(value: any, unit?: string): string {
    if (value == null) {
      return '-';
    }

    return unit ? `${value} ${unit}` : `${value}`;
  }

  /**
   * Converts seconds to a human-readable duration string (years, months, days, hours, minutes, seconds).
   * @param seconds Number of seconds to convert.
   * @returns Formatted duration string.
   */
  formatDurationFromSeconds(seconds: number): string {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
    const format = ['years', 'months', 'days', 'hours', 'minutes', 'seconds']
      .filter((unit) => duration[unit] && duration[unit] > 0)
      .slice(0, 3) as (keyof typeof duration)[];
    return formatDuration(duration, { format });
  }

  export(): void {
    this.executeExport(this.filterService.filterCriteriaValue);
  }

  navigateToUrl(url: string | null): void {
    if (url != null) {
      this.router.navigateByUrl(url);
    }
  }

  private executeExport(filter: AdmFilterCriteria): void {
    this.admService.downloadExport(this.building, filter).subscribe((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      a.href = url;
      a.download = this.getFileName(filter.endDateUTC);
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  private getFileName(endDate: Date): string {
    let base = `${this.building.name} Adm Data `;

    if (endDate) {
      const endDateFromBuilding = TimeUtils.convertTimezone(
        endDate,
        TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
      );
      base += `${format(endDateFromBuilding, 'dd-MM-yyyy')}`;
    } else {
      const buildingDate = TimeUtils.convertTimezone(
        new Date(),
        TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
      );
      base += `${format(buildingDate, 'dd-MM-yyyy')}`;
    }
    return base;
  }
}
